import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Fab, Typography } from '@mui/material';

// Icons
import { Close } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory } from 'react-router-dom';
import useImage from 'services/Hooks/useImage';
import { v4 as uuidv4 } from 'uuid';
import { usePostCarItem } from 'services/Data/CartService';
import { useSelector } from 'react-redux';
import { useGetAllValidGraphicForInventoryItems, useInventoryItem } from 'services/Data/InventoryItemServices';
import CartRedirectionLinkedItemCard from './CartRedirectionLinkedItemCard';

const useStyles = makeStyles(theme => ({
  marginTop10: {
    marginTop: 10,
  },
  modalCloseButton: {
    float: 'right',
    marginTop: -6,
  },
  modalTitle: {
    minimumHeight: 50,
  },
}));

const getNumericPrice = value => {
  const price = Number(value);
  return Number.isFinite(price) ? price : 0;
};

const isPodLinkedBundledItem = bundledItem =>
  bundledItem?.linkedToPod === true ||
  bundledItem?.linkedToPOD === true ||
  bundledItem?.LinkedToPOD === true ||
  bundledItem?.isLinkedToPOD === true ||
  bundledItem?.IsLinkedToPOD === true;

const CartRedirectionModal = ({ show, onClose, redirectLink, title, message, cartId, inventoryItemId, currencyName }) => {
  const { t } = useTranslation();
  const accountReducer = useSelector(state => state.AccountReducer);
  const classes = useStyles();
  const history = useHistory();
  const { getInventoryItemImage } = useImage();
  const { data: linkedItem } = useGetAllValidGraphicForInventoryItems(inventoryItemId);
  const { data: inventoryItem } = useInventoryItem(inventoryItemId);

  const itemsToDisplayPOD = inventoryItem?.bundledInventoryItems?.filter(isPodLinkedBundledItem) || [];
  const linkedPricingByConfiguredId = useMemo(() => {
    const map = new Map();
    itemsToDisplayPOD.forEach(item => {
      if (item?.additionalInventoryItemConfigured_Id) {
        map.set(item.additionalInventoryItemConfigured_Id, {
          price: getNumericPrice(item?.price),
          promotionPrice: getNumericPrice(item?.promotionPrice),
          promotionQuantity: item?.promotionQuantity,
          isInPromotion: item?.isInPromotion,
          name: item?.name,
        });
      }
    });
    return map;
  }, [itemsToDisplayPOD]);

  const resolveLinkedPricing = useCallback(
    item => {
      const bundledPricing = linkedPricingByConfiguredId.get(item?.id);
      return {
        price: getNumericPrice(bundledPricing?.price ?? item?.price),
        promotionPrice: getNumericPrice(bundledPricing?.promotionPrice ?? item?.promotionPrice),
        promotionQuantity: bundledPricing?.promotionQuantity ?? item?.promotionQuantity,
        isInPromotion: bundledPricing?.isInPromotion ?? item?.isInPromotion,
        name: item?.name || bundledPricing?.name || item?.dealerPortalTitleFr || item?.dealerPortalTitleEn || '',
        currencyName: item?.currencyName || currencyName || '',
      };
    },
    [currencyName, linkedPricingByConfiguredId],
  );

  const hasLinkedItems = Array.isArray(linkedItem) && linkedItem.length > 0;
  const { mutate: addCartItem } = usePostCarItem(accountReducer.isDemo);
  const resolvedCartId = cartId;
  const [addedItemIds, setAddedItemIds] = useState(() => new Set());
  const [selectedItemQuantities, setSelectedItemQuantities] = useState(() => new Map());
  const handleRedirect = useCallback(() => {
    history.push(redirectLink);
    onClose(true);
  }, [onClose, redirectLink, history]);

  const handleClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleAddToCart = useCallback(
    (inventoryItem, quantity = 1) => {
      if (!inventoryItem?.id) {
        return;
      }
      const pricing = resolveLinkedPricing(inventoryItem);

      const cartItem = {
        id: uuidv4(),
        inventoryItem_Id: inventoryItem.id,
        quantity,
        unitPrice: pricing.price,
        currencyName: pricing.currencyName,
        cart_Id: resolvedCartId,
        unitPricePromotion: pricing.promotionPrice,
        promotionQuantity: pricing.promotionQuantity,
        isInPromotion: pricing.isInPromotion,
        imgSrc: inventoryItem?.files.find(i => i.dealerPortalPrimary)
          ? inventoryItem?.files.find(i => i.dealerPortalPrimary)
          : inventoryItem?.files[0],
        name: pricing.name,
      };
      addCartItem(cartItem);
      setAddedItemIds(prev => {
        const next = new Set(prev);
        next.add(inventoryItem.id);
        return next;
      });
    },
    [addCartItem, resolveLinkedPricing, resolvedCartId, setAddedItemIds],
  );

  const handleToggleSelect = useCallback(itemId => {
    if (!itemId) {
      return;
    }

    setSelectedItemQuantities(prev => {
      const next = new Map(prev);
      const currentQuantity = next.get(itemId) || 0;
      if (currentQuantity > 0) {
        next.delete(itemId);
      } else {
        next.set(itemId, 1);
      }
      return next;
    });
  }, []);

  const handleIncreaseQuantity = useCallback(itemId => {
    if (!itemId) {
      return;
    }

    setSelectedItemQuantities(prev => {
      const next = new Map(prev);
      const currentQuantity = next.get(itemId) || 0;
      next.set(itemId, currentQuantity + 1);
      return next;
    });
  }, []);

  const handleDecreaseQuantity = useCallback(itemId => {
    if (!itemId) {
      return;
    }

    setSelectedItemQuantities(prev => {
      const next = new Map(prev);
      const currentQuantity = next.get(itemId) || 0;
      if (currentQuantity <= 1) {
        next.delete(itemId);
      } else {
        next.set(itemId, currentQuantity - 1);
      }
      return next;
    });
  }, []);

  const itemsTotal = useMemo(() => {
    return (linkedItem ?? []).reduce((total, item) => {
      const quantity = selectedItemQuantities.get(item?.id) || 0;
      return total + quantity * resolveLinkedPricing(item).price;
    }, 0);
  }, [linkedItem, resolveLinkedPricing, selectedItemQuantities]);

  const handleAddSelected = useCallback(() => {
    if (!selectedItemQuantities.size) {
      return;
    }

    (linkedItem ?? []).forEach(item => {
      if (!item?.id) {
        return;
      }
      const selectedQuantity = selectedItemQuantities.get(item.id) || 0;
      if (selectedQuantity > 0) {
        handleAddToCart(item, selectedQuantity);
      }
    });

    setSelectedItemQuantities(new Map());
  }, [handleAddToCart, linkedItem, selectedItemQuantities]);

  if (!show) return null;
  return (
    <Dialog
      open={show}
      maxWidth={'lg'}
      fullWidth={true}
      onClose={handleClose}
      aria-labelledby="max-width-dialog-title"
      aria-describedby="modal-slide-description">
      <DialogTitle>
        <Fab size="small" className={classes.modalCloseButton} key="close" aria-label="Close" onClick={handleClose}>
          <Close className={classes.modalClose} />
        </Fab>
        <Typography variant="h1" sx={{ fontSize: { xs: 18, sm: 20, md: 24 } }}>
          {title}
        </Typography>
        <Divider className={classes.marginTop10} />
      </DialogTitle>
      <DialogContent id="modal-slide-description" className={classes.modalBody}>
        <Box
          mb={3}
          dangerouslySetInnerHTML={{
            __html: message,
          }}
        />
        {hasLinkedItems && (
          <>
            <Typography variant="h4" gutterBottom>
              {t('linkedItemsAdded')}
            </Typography>
            {itemsToDisplayPOD
              .reduce((groups, pod) => {
                const width = pod?.itemWidth ?? null;
                const height = pod?.itemHeight ?? null;
                const key = `${width ?? 'unknown'}x${height ?? 'unknown'}`;

                if (!groups.find(group => group.key === key)) {
                  groups.push({ key, width, height });
                }

                return groups;
              }, [])
              .map(group => {
                const groupedItems = (linkedItem ?? []).filter(
                  item => item.itemWidth === group.width && item.itemHeight === group.height,
                );
                if (!groupedItems.length) {
                  return null;
                }
                const dimensionLabel =
                  group.width != null && group.height != null ? `${group.width} x ${group.height}` : t('sizeUnavailable');
                return (
                  <Box key={group.key} mb={3}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('sizeLabel')}: {dimensionLabel}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Box
                      display="grid"
                      gap={1.5}
                      sx={{
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          md: 'repeat(3, minmax(0, 1fr))',
                        },
                      }}>
                      {groupedItems.map((item, index) => {
                        const itemId = item?.id;
                        const pricing = resolveLinkedPricing(item);
                        const isItemAdded = itemId ? addedItemIds.has(itemId) : false;
                        const selectedQuantity = itemId ? selectedItemQuantities.get(itemId) || 0 : 0;
                        const isSelected = selectedQuantity > 0;
                        const cardItem = {
                          ...item,
                          name: pricing.name,
                          price: pricing.price,
                          promotionPrice: pricing.promotionPrice,
                          currencyName: pricing.currencyName,
                          isInPromotion: pricing.isInPromotion,
                          promotionQuantity: pricing.promotionQuantity,
                          PODAdditionalInfoEn: item?.PODAdditionalInfoEn || item?.podAdditionalInfoEn || '',
                          PODAdditionalInfoFr: item?.PODAdditionalInfoFr || item?.podAdditionalInfoFr || '',
                        };
                        return (
                          <CartRedirectionLinkedItemCard
                            key={item?.id ?? `${group.key}-${index}`}
                            item={cardItem}
                            index={index}
                            isSelected={isSelected}
                            selectedQuantity={selectedQuantity}
                            isItemAdded={isItemAdded}
                            onToggleSelect={handleToggleSelect}
                            onIncreaseQuantity={handleIncreaseQuantity}
                            onDecreaseQuantity={handleDecreaseQuantity}
                            getImageUrl={getInventoryItemImage}
                            currencyName={pricing.currencyName}
                            t={t}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Box alignSelf="center" ml={2} fontWeight={600} mr={2}>
                {t('itemsTotal')}: {itemsTotal.toFixed(2)}$ {currencyName || ''}
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleAddSelected}
                disabled={!selectedItemQuantities.size}
                style={{ color: 'black', borderRadius: 0 }}
                sx={{ borderRadius: 0, fontWeight: 700 }}>
                {t('addSelectedToCart')}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.modalFooter}>
        <Button
          onClick={handleRedirect}
          color="secondary"
          variant="contained"
          style={{ color: 'black' }}
          sx={{
            borderRadius: 0,
            pb: 2,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#EFBA29',
              boxShadow: 'none',
            },
          }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 } }}>
            {t('goToCart')}
          </Typography>
        </Button>
        <Button onClick={handleClose} color="primary">
          <Typography variant="h4">{t('keepShopping')}</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default CartRedirectionModal;
